/**
 * thread-stream.ts — merge delivered messages and in-flight/pending sends into ONE
 * chronologically-ordered render stream for the conversation thread.
 *
 * WHY (chatter-reported 2026-07-13 "messages are in chaotic order"): the thread was
 * built by rendering the `message` list first and then appending the `pendingSends`
 * list wholesale. That is only correct when every pending send is the newest thing in
 * the thread. It is NOT: an `outbound_send` in state 'sent' (Maloum ACKed it, but the
 * reconciler hasn't materialized the `message` row yet — routine on prod while
 * Cloudflare throttles the reconciler) keeps its real, possibly-older createdAt. So a
 * CRM send from 12:13 rendered *below* a delivered message from 12:15, and the thread
 * read out of order.
 *
 * Fix: interleave both streams by their real timestamp — `message.sentAt` for delivered
 * rows, `outbound_send.createdAt` for pending rows. This needs no per-state special
 * casing: a just-clicked 'confirmed'/'sending' send has createdAt≈now, so it still sorts
 * to the very bottom; a 'sent'/'send_failed' row from earlier sorts to where it actually
 * happened. The two source arrays are unchanged (the server still returns them
 * separately) — only the render order is unified.
 *
 * Kept pure and dependency-free (structural generics, no React/DOM/api-client imports)
 * so the ordering is unit-testable without a component-test harness.
 */

/** A delivered message carries the Maloum send time. */
type Timed = { sentAt: string };
/** A pending/failed outbound send carries the CRM row-creation time. */
type Pending = { createdAt: string };

export type ThreadStreamItem<M, P> =
  | { kind: 'message'; ts: number; item: M }
  | { kind: 'pending'; ts: number; item: P };

/** ISO string → epoch ms; a malformed/absent timestamp sorts LAST (never floats a bubble to the top). */
function toEpoch(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/**
 * Merge `messages` (by sentAt) and `pendingSends` (by createdAt) into one ascending,
 * oldest→newest stream.
 *
 * Ties: `Array.prototype.sort` is stable (ES2019+), and messages are concatenated before
 * pending sends — so at an identical timestamp a delivered/native bubble renders just
 * above a still-in-flight one, which is the natural reading order. Within each source the
 * caller's existing order is preserved.
 */
export function mergeThreadStream<M extends Timed, P extends Pending>(
  messages: readonly M[],
  pendingSends: readonly P[],
): ThreadStreamItem<M, P>[] {
  const items: ThreadStreamItem<M, P>[] = [];
  for (const m of messages) items.push({ kind: 'message', ts: toEpoch(m.sentAt), item: m });
  for (const p of pendingSends) items.push({ kind: 'pending', ts: toEpoch(p.createdAt), item: p });
  return items.sort((a, b) => a.ts - b.ts);
}
